import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { normalizeText } from '../utils/text';
import '../App.css';
import './ui/Button.css';

export interface Column<T> {
  header: string;
  render?: (item: T) => React.ReactNode;
  key?: string; // Optional key for simple property access
  /**
   * Value used for sorting this column. When provided (or when `key` is set)
   * the column header becomes clickable to toggle asc → desc → unsorted.
   */
  sortValue?: (item: T) => string | number | null;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
  onAnonymize?: (item: T) => void;
  editLink?: (item: T) => string;
  /** Extra per-row controls in the actions cell, before delete. Also counts
   * toward whether that column exists. */
  renderActions?: (item: T) => React.ReactNode;
  idField?: string;
  selectable?: boolean;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
  /** Render a search input above the table (client-side filtering over `data`). */
  searchable?: boolean;
  /**
   * Returns the searchable text for a row. Defaults to the concatenation of all
   * `col.key` values plus `item.name`.
   */
  searchAccessor?: (item: T) => string;
}

type SortDir = 'asc' | 'desc';

const DataTable = <T extends { [key: string]: any }>({
  data,
  columns,
  onEdit,
  onDelete,
  onAnonymize,
  editLink,
  renderActions,
  idField = '_id',
  selectable = false,
  selectedIds,
  onSelectionChange,
  searchable = false,
  searchAccessor,
}: DataTableProps<T>) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ index: number; dir: SortDir } | null>(null);

  const isSortable = (col: Column<T>) => Boolean(col.sortValue || col.key);

  const getSortValue = (col: Column<T>, item: T): string | number | null => {
    if (col.sortValue) return col.sortValue(item);
    if (col.key) {
      const v = item[col.key];
      return v == null ? null : (v as string | number);
    }
    return null;
  };

  const defaultSearchText = (item: T): string => {
    const parts: string[] = [];
    columns.forEach((col) => {
      if (col.key && item[col.key] != null) parts.push(String(item[col.key]));
    });
    if (item.name != null) parts.push(String(item.name));
    return parts.join(' ');
  };

  // Client-side filtering over the `data` prop.
  const filteredData = useMemo(() => {
    if (!searchable || search.trim() === '') return data;
    const needle = normalizeText(search.trim());
    return data.filter((item) => {
      const haystack = searchAccessor ? searchAccessor(item) : defaultSearchText(item);
      return normalizeText(haystack).includes(needle);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, search, searchable, searchAccessor]);

  // Client-side sorting over the filtered view.
  const sortedData = useMemo(() => {
    if (!sort) return filteredData;
    const col = columns[sort.index];
    if (!col || !isSortable(col)) return filteredData;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filteredData].sort((a, b) => {
      const av = getSortValue(col, a);
      const bv = getSortValue(col, b);
      // null/undefined always sort last regardless of direction.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * dir;
      }
      return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * dir;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData, sort, columns]);

  const handleSort = (index: number) => {
    const col = columns[index];
    if (!isSortable(col)) return;
    setSort((prev) => {
      if (!prev || prev.index !== index) return { index, dir: 'asc' };
      if (prev.dir === 'asc') return { index, dir: 'desc' };
      return null; // third click clears
    });
  };

  const allSelected = selectable && sortedData.length > 0 && selectedIds
    ? sortedData.every(item => selectedIds.has(item[idField]))
    : false;

  const handleSelectAll = () => {
    if (!onSelectionChange || !selectedIds) return;
    if (allSelected) {
      // Deselect all visible (filtered) rows
      const visibleIds = new Set(sortedData.map(item => item[idField] as string));
      const newSelection = new Set(Array.from(selectedIds).filter(id => !visibleIds.has(id)));
      onSelectionChange(newSelection);
    } else {
      // Select all visible (filtered) rows
      const newSelection = new Set(selectedIds);
      sortedData.forEach(item => newSelection.add(item[idField] as string));
      onSelectionChange(newSelection);
    }
  };

  const handleSelectRow = (item: T) => {
    if (!onSelectionChange || !selectedIds) return;
    const id = item[idField] as string;
    const newSelection = new Set(selectedIds);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    onSelectionChange(newSelection);
  };

  const colSpan =
    columns.length +
    (selectable ? 1 : 0) +
    (onEdit || onDelete || editLink || renderActions ? 1 : 0);

  return (
    <div className="table-container">
      {searchable && (
        <div className="data-table-search" style={{ marginBottom: '1rem' }}>
          <input
            type="text"
            className="data-table-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`🔍 ${t('common.search')}`}
            aria-label={t('common.search')}
          />
        </div>
      )}
      <table className="data-table" role="table">
        <thead>
          <tr role="row">
            {selectable && (
              <th role="columnheader" scope="col" style={{ width: '40px' }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={handleSelectAll}
                  aria-label={t('common.selectAll')}
                />
              </th>
            )}
            {columns.map((col, index) => {
              const sortable = isSortable(col);
              const active = sort?.index === index;
              return (
                <th
                  key={index}
                  role="columnheader"
                  scope="col"
                  onClick={sortable ? () => handleSort(index) : undefined}
                  aria-sort={active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  style={sortable ? { cursor: 'pointer', userSelect: 'none' } : undefined}
                >
                  {col.header}
                  {sortable && (
                    <i
                      className={`fas ${
                        active
                          ? sort!.dir === 'asc'
                            ? 'fa-chevron-up'
                            : 'fa-chevron-down'
                          : 'fa-sort'
                      } data-table__sort-icon${active ? ' data-table__sort-icon--active' : ''}`}
                      aria-hidden="true"
                    />
                  )}
                </th>
              );
            })}
            {(onEdit || onDelete || editLink || renderActions) && <th role="columnheader" scope="col">{t('common.actions')}</th>}
          </tr>
        </thead>
        <tbody>
          {sortedData.length === 0 ? (
            <tr role="row">
              <td role="cell" colSpan={colSpan} style={{ textAlign: 'center', fontStyle: 'italic', color: 'var(--text-muted)' }}>
                {t('common.noResults')}
              </td>
            </tr>
          ) : (
            sortedData.map((item) => (
              <tr key={item[idField]} role="row">
                {selectable && (
                  <td role="cell">
                    <input
                      type="checkbox"
                      checked={selectedIds?.has(item[idField]) ?? false}
                      onChange={() => handleSelectRow(item)}
                      aria-label={item.name ? `${t('common.select')} ${item.name}` : t('common.select')}
                    />
                  </td>
                )}
                {columns.map((col, index) => (
                  <td key={index} role="cell">
                    {col.render ? col.render(item) : col.key ? item[col.key] : null}
                  </td>
                ))}
                {(onEdit || onDelete || editLink || renderActions) && (
                  <td role="cell">
                    <div className="grid-actions">
                      {editLink && (
                        <Link
                          to={editLink(item)}
                          className="btn btn--primary btn--sm"
                          aria-label={item.name ? `${t('common.edit')} ${item.name}` : t('common.edit')}
                        >
                          <i className="fas fa-edit" aria-hidden="true"></i>
                        </Link>
                      )}
                      {onEdit && (
                        <button
                          onClick={() => onEdit(item)}
                          className="btn btn--primary btn--sm"
                          type="button"
                          aria-label={item.name ? `${t('common.edit')} ${item.name}` : t('common.edit')}
                        >
                          <i className="fas fa-edit" aria-hidden="true"></i>
                        </button>
                      )}
                      {renderActions?.(item)}
                      {onAnonymize && (
                        <button
                          onClick={() => onAnonymize(item)}
                          className="btn btn--danger btn--sm"
                          type="button"
                          aria-label={item.name ? `${t('members.anonymize')} ${item.name}` : t('members.anonymize')}
                          title={t('members.anonymize')}
                        >
                          <i className="fas fa-eye-slash" aria-hidden="true"></i>
                        </button>
                      )}
                      {onDelete && (
                        <button
                          onClick={() => onDelete(item)}
                          className="btn btn--danger btn--sm"
                          type="button"
                          aria-label={item.name ? `${t('common.delete')} ${item.name}` : t('common.delete')}
                          title={t('common.delete')}
                        >
                          <i className="fas fa-trash" aria-hidden="true"></i>
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DataTable;
